import * as React from 'react';
import Box from '@mui/material/Box';
import RtlButton from './RtlButton';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router';
import { useLanguage } from '../../contexts/LanguageContext';
import { ArrowForward } from '@mui/icons-material';
import type { Order } from '../data/orders';

export interface OrderFormState {
  values: Partial<Omit<Order, 'id'>> & { price_per_person?: number };
  errors: Partial<Record<keyof OrderFormState['values'], string>>;
}

export type FormFieldValue = string | string[] | number | boolean | File | null | undefined;

export interface OrderFormProps {
  formState: OrderFormState;
  onFieldChange: (
    name: keyof OrderFormState['values'],
    value: FormFieldValue,
  ) => void;
  onSubmit: (formValues: Partial<OrderFormState['values']>) => Promise<void>;
  onReset?: (formValues: Partial<OrderFormState['values']>) => void;
  submitButtonLabel: string;
  backButtonPath?: string;
}

export default function OrderForm(props: OrderFormProps) {
  const {
    formState,
    onFieldChange,
    onSubmit,
    onReset,
    submitButtonLabel,
    backButtonPath,
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const navigate = useNavigate();
  const { t, direction } = useLanguage();
  const isRtl = direction === 'rtl';

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const lastCalculatedTotal = React.useRef<number>(0);

  // Auto-calculate full_amount when both people_number and price_per_person are
  // provided. cost, price_per_person and full_amount are all optional, so we
  // avoid overwriting full_amount when either input is missing.
  React.useEffect(() => {
    const peopleNumber = formValues.people_number;
    const pricePerPerson = formValues.price_per_person;

    if (!peopleNumber || peopleNumber <= 0 || !pricePerPerson || pricePerPerson <= 0) {
      return;
    }

    const calculatedTotal = peopleNumber * pricePerPerson;

    if (calculatedTotal !== lastCalculatedTotal.current) {
      lastCalculatedTotal.current = calculatedTotal;
      onFieldChange('full_amount', calculatedTotal);
    }
  }, [formValues.people_number, formValues.price_per_person, onFieldChange]);

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setIsSubmitting(true);
      try {
        await onSubmit(formValues);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, onSubmit],
  );

  const handleNumberFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      onFieldChange(
        event.target.name as keyof OrderFormState['values'],
        value === '' ? undefined : Number(value),
      );
    },
    [onFieldChange],
  );

  const handleTextFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof OrderFormState['values'],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleReset = React.useCallback(() => {
    if (onReset) {
      onReset(formValues);
    }
  }, [formValues, onReset]);

  const handleBack = React.useCallback(() => {
    navigate(backButtonPath ?? '/orders');
  }, [navigate, backButtonPath]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          {/* Order Information Section */}
          <Grid size={{ xs: 12 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                mt: 1,
                display: 'flex', 
                alignItems: 'center',
                color: 'primary.main',
                fontWeight: 600
              }}
            >
              <ReceiptIcon sx={{ mr: 1 }} />
              {t('orders.details')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          {/* Customer Name */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <TextField
              type="text"
              value={formValues.customerName ?? ''}
              onChange={handleTextFieldChange}
              name="customerName"
              label={t('orders.customerName')}
              error={!!formErrors.customerName}
              helperText={formErrors.customerName ?? ' '}
              fullWidth
              required
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left'
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>

          {/* Customer Number */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <TextField
              type="tel"
              value={formValues.customerNumber ?? ''}
              onChange={handleTextFieldChange}
              name="customerNumber"
              label={t('orders.customerNumber')}
              error={!!formErrors.customerNumber}
              helperText={formErrors.customerNumber ?? ' '}
              fullWidth
              required
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left'
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>

          {/* Destination */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <TextField
              type="text"
              value={formValues.destination ?? ''}
              onChange={handleTextFieldChange}
              name="destination"
              label={t('orders.destination')}
              error={!!formErrors.destination}
              helperText={formErrors.destination ?? ' '}
              fullWidth
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left'
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>

          {/* Cost */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <TextField
              type="number"
              value={formValues.cost ?? ''}
              onChange={handleNumberFieldChange}
              name="cost"
              label={t('orders.cost')}
              error={!!formErrors.cost}
              helperText={formErrors.cost ?? ' '}
              fullWidth
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left'
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>

          {/* Pricing Section */}
          <Grid size={{ xs: 12 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                mt: 3,
                display: 'flex', 
                alignItems: 'center',
                color: 'primary.main',
                fontWeight: 600
              }}
            >
              <AttachMoneyIcon sx={{ mr: 1 }} />
              {t('orders.pricing')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          {/* Number of People */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
            <TextField
              type="number"
              value={formValues.people_number ?? ''}
              onChange={handleNumberFieldChange}
              name="people_number"
              label={t('orders.people_number')}
              error={!!formErrors.people_number}
              helperText={formErrors.people_number ?? ' '}
              fullWidth
              required
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left'
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>

          {/* Price Per Person */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
            <TextField
              type="number"
              value={formValues.price_per_person ?? ''}
              onChange={handleNumberFieldChange}
              name="price_per_person"
              label={t('orders.pricePerPerson')}
              error={!!formErrors.price_per_person}
              helperText={formErrors.price_per_person ?? ' '}
              fullWidth
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left'
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>

          {/* Total Amount (Read-only, Auto-calculated) */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
            <TextField
              type="number"
              value={formValues.full_amount ?? ''}
              name="full_amount"
              label={t('orders.totalAmount')}
              error={!!formErrors.full_amount}
              helperText={formErrors.full_amount ?? ' '}
              fullWidth
              disabled
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left'
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>
        </Grid>
      </FormGroup>
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <RtlButton
          type="submit"
          variant="contained"
          size="large"
          loading={isSubmitting}
          sx={{ background: '#028bcb !important', boxShadow: 'unset !important', border: '0 !important', '&:hover': { background: '#0277a8 !important' } }}
        >
          {submitButtonLabel}
        </RtlButton>
      </Stack>
    </Box>
  );
}
